import { useEffect, useId, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { loginInputSchema, type LoginInput } from '@underline/shared';
import { ease } from '@/lib/motion';
import { FloatingField } from '@/components/ui/floating-field';
import { InlineWordmark } from '@/components/layout/inline-wordmark';
import { useLogin, useMe } from '@/hooks/use-auth';
import { ForceChangePasswordModal } from '@/components/users/force-change-password-modal';
import { LoginSplash } from '@/components/auth/login-splash';
import { subscribeToPush } from '@/lib/push-permission';

interface LocationState {
  from?: { pathname?: string };
}


export function LoginPage() {
  const { isAuthenticated, isLoading } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [submitting, setSubmitting] = useState(false);
  const [showForceChange, setShowForceChange] = useState(false);
  const [splash, setSplash] = useState<{ firstName: string; redirect: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const pwId = useId();

  // Trava o scroll e pinta o html/body de preto pra não vazar bg branco na safe-area do PWA.
  useEffect(() => {
    const prevBodyOv = document.body.style.overflow;
    const prevHtmlOv = document.documentElement.style.overflow;
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.backgroundColor = '#000';
    document.documentElement.style.backgroundColor = '#000';
    return () => {
      document.body.style.overflow = prevBodyOv;
      document.documentElement.style.overflow = prevHtmlOv;
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { username: '', password: '' },
  });

  if (!isLoading && isAuthenticated && !showForceChange && !splash) {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  async function doLogin(values: LoginInput) {
    setSubmitting(true);
    try {
      await new Promise<void>((resolve, reject) => {
        login.mutate(values, {
          onSuccess: (data) => {
            // Pede permissão de notificação (fire-and-forget; falha silenciosa em browsers que não suportam)
            void subscribeToPush().catch(() => {});
            if (data.must_change_password) {
              setShowForceChange(true);
            } else {
              const from =
                (location.state as LocationState | null)?.from?.pathname ?? '/dashboard';
              setSplash({ firstName: data.user.name.split(' ')[0] ?? '', redirect: from });
            }
            resolve();
          },
          onError: (err) => {
            toast.error(err.message || 'Falha no login');
            reject(err);
          },
        });
      });
    } catch {
      // erro tratado no toast acima
    } finally {
      setSubmitting(false);
    }
  }

  function onPasswordChanged() {
    setShowForceChange(false);
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/dashboard';
    navigate(from, { replace: true });
  }

  return (
    <>
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-background">
        {/* Topo: vídeo cobre ~50% e dissolve para o fundo */}
        <div className="relative h-1/2 min-h-[320px] overflow-hidden bg-[#0a0a0a]">
          <video
            src="/bg-login.webm"
            autoPlay
            loop
            muted
            playsInline
            poster="/bg-login.jpg"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
          {/* Overlay escuro entre o vídeo e o gradiente */}
          <div className="pointer-events-none absolute inset-0 bg-black/40" aria-hidden />
          {/* Gradiente que dissolve para bg-background */}
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 12%, color-mix(in oklab, var(--background) 0%, transparent) 22%, color-mix(in oklab, var(--background) 40%, transparent) 42%, color-mix(in oklab, var(--background) 85%, transparent) 62%, var(--background) 78%)',
            }}
          />
        </div>

        {/* Form — sobreposto ao vídeo via margin negativa */}
        <div className="relative mx-auto -mt-60 flex w-full max-w-[420px] flex-1 flex-col overflow-y-auto px-6 pb-[calc(env(safe-area-inset-bottom,0px)+24px)]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
          >
            <InlineWordmark height={16} color="#fff" stagger={70} style={{ marginBottom: 36 }} />
            <p className="eyebrow font-semibold text-primary">login //// painel interno</p>
            <h1 className="mt-2.5 font-display text-[26px] font-bold leading-[1.18] tracking-[-0.02em] text-foreground">
              Gestão de agenda e postagens em um só lugar!
            </h1>
          </motion.div>

          <form onSubmit={handleSubmit(doLogin)} className="mt-8 flex flex-col gap-3.5">
            <FloatingField
              label="Usuário"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              placeholder="voce@underline.com"
              error={errors.username?.message}
              {...register('username')}
            />

            <div className="w-full">
              <label
                htmlFor={pwId}
                className="mb-2 block font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
              >
                Senha
              </label>
              <div className="relative">
                <input
                  id={pwId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password')}
                  className={`w-full rounded-lg border bg-card px-3.5 py-3 pr-11 text-base text-foreground outline-none transition-colors focus:border-foreground ${errors.password ? 'border-destructive' : 'border-border'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password?.message && (
                <p className="mt-1.5 text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              type="submit"
              disabled={submitting}
              className="mt-1.5 w-full rounded-lg bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60"
            >
              {submitting ? 'Entrando...' : 'Entrar'}
            </motion.button>
          </form>

          <p className="mt-auto pt-8 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
            Underline_ Marketing &amp; Eventos
          </p>
        </div>
      </div>

      <ForceChangePasswordModal open={showForceChange} onDone={onPasswordChanged} />

      {splash && (
        <LoginSplash
          firstName={splash.firstName}
          onDone={() => navigate(splash.redirect, { replace: true })}
        />
      )}
    </>
  );
}
